// Correction des traits trop fins (<0,5 mm) : même pipeline morphologique que
// thinlines.ts, mais au lieu d'alerter on répare — soit en épaississant les
// traits dans leur propre couleur (propagée depuis le pixel de trait le plus
// proche), soit en posant un fond couleur vêtement uniquement sous ces traits.
// Sortie = PNG raster (les SVG/PDF corrigés repassent en raster).
// 09.10.2026 (Daniel : « épaissir, ça fait de la merde ») : la poussière de détourage est effacée avant
// l'analyse (sinon chaque point devenait une pastille), la couleur est prise dans le cœur opaque du trait
// (pas dans son halo délavé), et le bord de l'épaississement est adouci sur 2 px au lieu d'être en escalier.
// 20.10.2026 (Daniel, logo « test » : « Détail » criait encore « traits fins » après Épaissir, et proposait 48,5 cm) :
// l'ANALYSE se fait désormais à la MÊME échelle que la détection (thinlines : 12 px/mm, 4 Mpx max) — avant, elle
// tournait à la résolution du fichier (16 px/mm sur une image améliorée), et à cette échelle les cous des gouttes
// n'étaient plus « fins » : la détection en trouvait 6, la correction 0, et ne changeait rien. Seul le DESSIN de
// l'épaississement se fait à la résolution de sortie. L'adoucissement du bord se fait vers l'intérieur de la marge,
// pour que l'encre ne dépasse jamais la zone épaissie (sinon un liseré d'un pixel restait « fin » à la relecture).
import { autoSpeckleArea, despeckle, dustAreaPx } from './despeckle';
import { DILATE_SLACK, keepRealThinStrokes, rasterize } from './thinlines';

const MIN_STROKE_MM = 0.5;
const GROW_MM = 0.35; // élargissement de chaque côté → trait final ≥ 0,7 mm (marge sur le seuil de 0,5 mm)
// échelle d'analyse = celle de thinlines.ts (la détection et la correction doivent voir les MÊMES traits)
const TARGET_PX_PER_MM = 12;
const MAX_ANALYSIS_PIXELS = 4_000_000;
const MAX_OUT_PIXELS = 12_000_000; // sous la limite canvas Safari (~16,7 Mpx)

export type ThinFixMode = 'thicken' | 'backing';

const loadImg = (src: string): Promise<HTMLImageElement> =>
	new Promise((resolve, reject) => {
		const im = new Image();
		im.onload = () => resolve(im);
		im.onerror = () => reject(new Error('image load failed'));
		im.src = src;
	});

// Chamfer 3-4 double passe ; si idx est fourni, propage aussi l'indice du pixel
// source le plus proche (feature transform) pour retrouver sa couleur.
const chamfer = (dist: Int32Array, w: number, h: number, idx?: Int32Array): void => {
	const relax = (i: number, j: number, cost: number) => {
		if (dist[j] + cost < dist[i]) {
			dist[i] = dist[j] + cost;
			if (idx) idx[i] = idx[j];
		}
	};
	for (let y = 0; y < h; y++) {
		for (let x = 0; x < w; x++) {
			const i = y * w + x;
			if (x > 0) relax(i, i - 1, 3);
			if (y > 0) {
				relax(i, i - w, 3);
				if (x > 0) relax(i, i - w - 1, 4);
				if (x < w - 1) relax(i, i - w + 1, 4);
			}
		}
	}
	for (let y = h - 1; y >= 0; y--) {
		for (let x = w - 1; x >= 0; x--) {
			const i = y * w + x;
			if (x < w - 1) relax(i, i + 1, 3);
			if (y < h - 1) {
				relax(i, i + w, 3);
				if (x < w - 1) relax(i, i + w + 1, 4);
				if (x > 0) relax(i, i + w - 1, 4);
			}
		}
	}
};

const hexToRgb = (hex: string): [number, number, number] => {
	const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
	const v = m ? parseInt(m[1], 16) : 0xffffff;
	// eslint-disable-next-line no-bitwise
	return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};

/**
 * Répare les traits sous 0,5 mm à la taille d'impression donnée.
 * Retourne null si aucun trait fin n'est trouvé (rien à faire) — avec la même analyse que la détection,
 * cela ne peut arriver que si la détection ne crie pas non plus à cette taille.
 */
export async function fixThinStrokes(
	src: string,
	widthCm: number,
	heightCm: number,
	mode: ThinFixMode,
	garmentColor = '#ffffff',
): Promise<{ src: string } | null> {
	// 20.10.2026 : la correction se relit elle-même (même analyse que la détection) et repasse si un trait fin
	// subsiste — le dessin épaissi change la géométrie autour des traits, un morceau juste sous le seuil peut
	// passer au-dessus. Au plus MAX_PASSES passes ; null = rien à corriger dès la première.
	let cur = src;
	let changed = false;
	for (let pass = 0; pass < MAX_PASSES; pass++) {
		await new Promise(r => setTimeout(r, 0)); // laisser le navigateur peindre (voile « Paddy travaille ») entre deux passes
		const out = await fixThinOnce(cur, widthCm, heightCm, mode, garmentColor);
		if (!out) break;
		cur = out;
		changed = true;
	}
	return changed ? { src: cur } : null;
}

const MAX_PASSES = 3;

async function fixThinOnce(
	src: string,
	widthCm: number,
	heightCm: number,
	mode: ThinFixMode,
	garmentColor: string,
): Promise<string | null> {
	const wMm = widthCm * 10;
	const hMm = heightCm * 10;
	if (wMm <= 0 || hMm <= 0) return null;

	const im = await loadImg(src);
	// --- analyse : mêmes px/mm et mêmes dimensions de rendu que hasThinStrokes ---
	const pxPerMm = Math.min(TARGET_PX_PER_MM, Math.sqrt(MAX_ANALYSIS_PIXELS / (wMm * hMm)));
	const r = (MIN_STROKE_MM / 2) * pxPerMm;
	const r3 = Math.round(3 * r);
	const g3 = Math.round(3 * GROW_MM * pxPerMm);
	const pad = Math.ceil(r + GROW_MM * pxPerMm) + 2;
	const iw = Math.max(1, Math.round(wMm * pxPerMm));
	const ih = Math.max(1, Math.round(hMm * pxPerMm));
	const W = iw + 2 * pad;
	const H = ih + 2 * pad;

	// même rastérisation déterministe que la détection (thinlines.rasterize) : mêmes pixels, mêmes traits
	const data = rasterize(im, iw, ih, pad, W, H);
	if (!data) return null;
	// seuil = celui du nettoyage automatique, en pixels SOURCE (la poussière a la taille du fichier, pas celle de
	// l'impression), ramené à l'échelle d'analyse ; plancher physique 0,1 mm²
	const srcArea = autoSpeckleArea(im.naturalWidth || 1, im.naturalHeight || 1) * ((iw * ih) / Math.max(1, (im.naturalWidth || 1) * (im.naturalHeight || 1)));
	despeckle(data, W, H, Math.max(dustAreaPx(pxPerMm), Math.round(srcArea)));

	const n = W * H;
	const INF = 0x3fffffff;
	const ink = new Uint8Array(n);
	const dist = new Int32Array(n);
	let inkCount = 0;
	for (let i = 0; i < n; i++) {
		if (data[i * 4 + 3] > 32) {
			ink[i] = 1;
			inkCount++;
			dist[i] = INF;
		}
	}
	if (!inkCount) return null;

	chamfer(dist, W, H);
	const dist2 = new Int32Array(n);
	let survivors = 0;
	for (let i = 0; i < n; i++) {
		if (ink[i] && dist[i] > r3) {
			dist2[i] = 0;
			survivors++;
		} else {
			dist2[i] = INF;
		}
	}

	// pixels de traits fins (mêmes règles que la détection)
	const isThin = new Uint8Array(n);
	let thin = 0;
	if (!survivors) {
		for (let i = 0; i < n; i++) if (ink[i]) { isThin[i] = 1; thin++; }
	} else {
		chamfer(dist2, W, H);
		for (let i = 0; i < n; i++) if (ink[i] && dist2[i] > r3 + DILATE_SLACK) { isThin[i] = 1; thin++; }
	}
	if (!thin) return null;

	// Daniel 09.10 (photo : pastilles aux pointes et aux coins d'un liseré) : seuls les VRAIS traits sont épaissis —
	// même règle que la détection (thinlines.keepRealThinStrokes)
	thin = keepRealThinStrokes(isThin, W, H, pxPerMm);
	if (!thin) return null;

	// distance à n'importe quel pixel de trait fin → masque d'épaississement
	const dist3 = new Int32Array(n).fill(INF);
	for (let i = 0; i < n; i++) if (isThin[i]) dist3[i] = 0;
	chamfer(dist3, W, H);

	// couleur : propagée depuis le cœur OPAQUE des traits fins (alpha ≥ 160) ; si le trait n'en a pas
	// (hairline entièrement anticrénelée), depuis tous ses pixels
	const distC = new Int32Array(n).fill(INF);
	const nearest = new Int32Array(n).fill(-1);
	let seeds = 0;
	for (let i = 0; i < n; i++) if (isThin[i] && data[i * 4 + 3] >= 160) { distC[i] = 0; nearest[i] = i; seeds++; }
	if (!seeds) for (let i = 0; i < n; i++) if (isThin[i]) { distC[i] = 0; nearest[i] = i; }
	chamfer(distC, W, H, nearest);

	// dilatation de GROW_MM, bord adouci sur ~2 px (6 unités chamfer) VERS L'INTÉRIEUR de la marge
	const layer = new ImageData(W, H);
	const ld = layer.data;
	const [gr, gg, gb] = hexToRgb(garmentColor);
	const feather = 6;
	for (let i = 0; i < n; i++) {
		const dd = dist3[i];
		if (dd > g3) continue;
		const o = i * 4;
		if (mode === 'backing') {
			ld[o] = gr;
			ld[o + 1] = gg;
			ld[o + 2] = gb;
		} else {
			const s = nearest[i] * 4;
			ld[o] = data[s];
			ld[o + 1] = data[s + 1];
			ld[o + 2] = data[s + 2];
		}
		ld[o + 3] = dd <= g3 - feather ? 255 : Math.round((255 * (g3 - dd)) / feather);
	}

	const layerCanvas = document.createElement('canvas');
	layerCanvas.width = W;
	layerCanvas.height = H;
	const lctx = layerCanvas.getContext('2d');
	if (!lctx) return null;
	lctx.putImageData(layer, 0, 0);

	// --- sortie : au moins la résolution du fichier (dans la limite canvas), l'épaississement est mis à l'échelle ---
	const outPxPerMm = Math.min(Math.max(TARGET_PX_PER_MM, im.naturalWidth / wMm), Math.sqrt(MAX_OUT_PIXELS / (wMm * hMm)));
	const ow = Math.max(1, Math.round(wMm * outPxPerMm));
	const oh = Math.max(1, Math.round(hMm * outPxPerMm));
	const out = document.createElement('canvas');
	out.width = ow;
	out.height = oh;
	const octx = out.getContext('2d');
	if (!octx) return null;
	octx.imageSmoothingEnabled = true;
	octx.imageSmoothingQuality = 'high';
	octx.drawImage(layerCanvas, pad, pad, iw, ih, 0, 0, ow, oh);
	// par-dessus : l'image d'origine (le nettoyage ne sert qu'à l'analyse — sans la couleur retirée, un petit
	// îlot peut être un point de « i » : on ne l'efface pas de la sortie)
	octx.drawImage(im, 0, 0, ow, oh);
	return out.toDataURL('image/png');
}
